import { VNZImageEntity } from "~/interfaces/entities/VNZImage"
import { PrinticularApi } from "~/services/PrinticularApi"

export class VNZService {
  private authorizationUrl: string
  private baseUrl: string
  private imageProxyUrl: string
  private printicularApi: PrinticularApi

  constructor(
    baseUrl: string,
    authorizationUrl: string,
    imageProxyUrl: string,
    printicularApi: PrinticularApi
  ) {
    this.baseUrl = baseUrl.replace(/\/+$/, "")
    this.authorizationUrl = authorizationUrl.replace(/\/+$/, "")
    this.imageProxyUrl = imageProxyUrl.replace(/\/+$/, "")
    this.printicularApi = printicularApi
  }

  public getAuthenticationUrl(clientId: number, deviceToken: string) {
    const state = encodeURIComponent(
      JSON.stringify({
        deviceToken,
        clientId,
      })
    )
    const url = `${this.authorizationUrl}&state=${state}`

    return url
  }

  public async getAccessToken(authorizationCode: string) {
    return this.printicularApi.getVerizonAccessToken(authorizationCode)
  }

  public async fullview(accessToken: string) {
    const result = await fetch(`${this.baseUrl}/vzcloud-apis/v1/fullview`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    const json = await result.json()
    const data: {
      count: number
      folder: string[]
      file: VNZImageEntity[]
    } = json.data

    return data
  }

  public async fetchAllImages(accessToken: string) {
    const allFilesResults = await this.fullview(accessToken)
    const allImagesResults = {
      count: 0,
      folder: [],
      file: [] as VNZImageEntity[],
    }

    const allImagesFiles = allFilesResults.file.filter(file =>
      file.systemAttributes?.MimeType?.startsWith("image/")
    )

    allImagesResults.count = allImagesFiles.length
    allImagesResults.file = allImagesFiles

    const allImages = allImagesResults.file.map(file => {
      return {
        ...file,
        id: file.checksum,
        isLoadingThumbnail: false,
        hasLoadedThumbnail: false,
        thumbnailUrl: `${this.baseUrl}/vzcloud-apis/v1/thumbnails/${file.contentToken}?size=l`,
        isLoadingFullImage: false,
        hasLoadedFullImage: false,
        fullImageUrl: `${this.imageProxyUrl}/vzcloud-apis/v1/files${file.parentPath}${file.name}`,
      }
    })

    return allImages
  }

  public async fetchThumbnail(accessToken: string, image: VNZImageEntity) {
    const imageResult = await fetch(image.thumbnailUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    const arrayBuffer = await imageResult.arrayBuffer()
    const blob = new Blob([arrayBuffer], {
      type: imageResult.headers.get("content-type") || "image/*",
    })
    const url = URL.createObjectURL(blob)

    return url
  }

  public async fetchFullImage(accessToken: string, image: VNZImageEntity) {
    const imageResult = await fetch(image.fullImageUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (
      imageResult.status.toString().startsWith("4") ||
      imageResult.status.toString().startsWith("5")
    ) {
      throw new Error(
        `Failed to fetch full image. Status: ${
          imageResult.status
        }, Message: ${await imageResult.text()}`
      )
    }

    const arrayBuffer = await imageResult.arrayBuffer()
    const blob = new Blob([arrayBuffer], {
      type: imageResult.headers.get("content-type") || "image/*",
    })
    const url = URL.createObjectURL(blob)

    return url
  }
}
